import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, Upload, Download, Trash2, Eye, FileText, FolderOpen,
  RefreshCw, Plus, X, File, Image, FileSpreadsheet,
  ChevronRight, Copy, Check, AlertTriangle, BarChart3,
  FolderPlus, Filter, MoreVertical
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/new-components/ui/select';
import api from '../../services/api';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */
interface DocumentMeta {
  id: string;
  tenant_id: string;
  folder: string;
  collection: string | null;
  category: string;
  title: string;
  description: string;
  original_filename: string;
  s3_bucket: string;
  s3_key: string;
  mime_type: string;
  file_size_bytes: number;
  uploaded_by: string;
  uploader_email?: string;
  engagement_id?: string;
  deal_id?: string;
  transaction_id?: string;
  is_signed_copy: boolean;
  source_template_id?: string;
  signed_by_email?: string;
  signed_by_name?: string;
  signed_at?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

interface DocStats {
  total_documents: number;
  templates: number;
  signed_copies: number;
  engagement_docs: number;
  uploads: number;
  total_bytes: number;
  total_signed: number;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */
const unwrap = (res: any) => {
  if (res?.data?.success !== undefined) return res.data;
  if (res?.success !== undefined) return res;
  return res?.data || res;
};

const fmtBytes = (b: number | string | null | undefined) => {
  const n = Number(b) || 0;
  if (n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
};

const fmtDate = (d: string) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const mimeIcon = (mime: string) => {
  if (mime?.startsWith('image/')) return <Image className="w-4 h-4" />;
  if (mime?.includes('spreadsheet') || mime?.includes('csv') || mime?.includes('excel'))
    return <FileSpreadsheet className="w-4 h-4" />;
  if (mime?.includes('pdf') || mime?.includes('word') || mime?.includes('document'))
    return <FileText className="w-4 h-4" />;
  return <File className="w-4 h-4" />;
};

const folderColor: Record<string, string> = {
  templates: 'bg-blue-100 text-blue-700',
  signed: 'bg-green-100 text-green-700',
  'engagement-docs': 'bg-purple-100 text-purple-700',
  uploads: 'bg-gray-100 text-gray-700',
  offerings: 'bg-amber-100 text-amber-700',
  properties: 'bg-teal-100 text-teal-700',
  offers: 'bg-amber-100 text-amber-700',
  assets: 'bg-teal-100 text-teal-700',
  'signed-documents': 'bg-green-100 text-green-700',
};

const CATEGORIES = ['general', 'legal', 'ppm', 'sub_docs', 'oa', 'k1', 'report'];
const FOLDERS = ['templates', 'signed', 'engagement-docs', 'uploads', 'offerings', 'properties', 'offers', 'assets', 'signed-documents'];

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */
const AdminDocuments: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentMeta[]>([]);
  const [stats, setStats] = useState<DocStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 25;

  // Filters
  const [search, setSearch] = useState('');
  const [folderFilter, setFolderFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [collectionFilter, setCollectionFilter] = useState('all');

  // Folders, and the current bulk selection.
  const [collections, setCollections] = useState<{ collection: string; documents: number }[]>([]);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [moveTo, setMoveTo] = useState('');
  const [newFolder, setNewFolder] = useState('');
  const [moving, setMoving] = useState(false);

  // Dialogs
  const [showUpload, setShowUpload] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selected, setSelected] = useState<DocumentMeta | null>(null);
  const [showSignCopy, setShowSignCopy] = useState(false);

  // Upload state
  const [uploadFolder, setUploadFolder] = useState('templates');
  const [uploadCategory, setUploadCategory] = useState('general');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sign-copy state
  const [signEmail, setSignEmail] = useState('');
  const [signName, setSignName] = useState('');
  const [signing, setSigning] = useState(false);

  // Init bucket
  const [initMsg, setInitMsg] = useState('');

  /* ───────── Data loading ────────────────────────────────────── */
  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page, limit };
      if (search) params.search = search;
      if (folderFilter !== 'all') params.folder = folderFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (collectionFilter !== 'all') params.collection = collectionFilter;

      // `api.get` is (url, body, config). Passing the query as the second
      // argument makes it a GET request body, which axios drops -- so every
      // filter and the paging on this page silently did nothing.
      const res = await api.get('/admin/documents', undefined, { params });
      const body = unwrap(res);
      setDocuments(body.documents || []);
      setTotal(body.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to load documents', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, folderFilter, categoryFilter, collectionFilter]);

  const loadCollections = useCallback(async () => {
    try {
      const body = unwrap(await api.get('/admin/documents/collections'));
      setCollections(body.data?.collections || []);
      setUnfiledCount(body.data?.unfiled || 0);
    } catch (err) {
      console.error('Failed to load folders', err);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/documents/stats');
      const body = unwrap(res);
      setStats(body.data || null);
    } catch (err) {
      console.error('Failed to load stats', err);
    }
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadCollections(); }, [loadCollections]);

  // Clear the selection whenever the visible set changes. Keeping it would
  // mean a "Move 20" button acting on documents that scrolled out of view
  // three filters ago.
  useEffect(() => { setChecked(new Set()); }, [page, search, folderFilter, categoryFilter, collectionFilter]);

  /* ───────── Selection ───────────────────────────────────────── */
  const toggleOne = (id: string) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const allShownChecked = documents.length > 0 && documents.every((d) => checked.has(d.id));
  const toggleAllShown = () => setChecked((prev) => {
    const next = new Set(prev);
    if (allShownChecked) documents.forEach((d) => next.delete(d.id));
    else documents.forEach((d) => next.add(d.id));
    return next;
  });

  /* ───────── Filing ──────────────────────────────────────────── */
  const fileSelected = async (destination: string | null) => {
    if (checked.size === 0) return;
    setMoving(true);
    try {
      const res: any = await api.post('/admin/documents/collections/move', {
        // Array.from, not spread: this build targets below es2015, where
        // spreading a Set iterator fails to typecheck.
        documentIds: Array.from(checked),
        collection: destination,
      });
      if (res?.success === false) {
        alert(res.error || 'Could not move the documents.');
        return;
      }
      setChecked(new Set());
      setNewFolder('');
      setMoveTo('');
      await Promise.all([loadDocuments(), loadCollections()]);
    } catch (err) {
      console.error('Move failed', err);
      alert('Could not move the documents.');
    } finally {
      setMoving(false);
    }
  };

  /* ───────── Actions ─────────────────────────────────────────── */
  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('folder', uploadFolder);
      fd.append('category', uploadCategory);
      fd.append('description', uploadDescription);

      await api.post('/admin/documents/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setShowUpload(false);
      setUploadFile(null);
      setUploadDescription('');
      loadDocuments();
      loadStats();
    } catch (err) {
      console.error('Upload failed', err);
      alert('Upload failed. Check console for details.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: DocumentMeta) => {
    try {
      const res = await api.get(`/admin/documents/${doc.id}/download`);
      const body = unwrap(res);
      window.open(body.data.url, '_blank');
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  const handleDelete = async (doc: DocumentMeta) => {
    if (!window.confirm(`Delete "${doc.title}"? This is a soft-delete — the file stays in S3.`)) return;
    try {
      await api.delete(`/admin/documents/${doc.id}`);
      loadDocuments();
      loadStats();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const handleSignCopy = async () => {
    if (!selected) return;
    setSigning(true);
    try {
      await api.post(`/admin/documents/${selected.id}/sign-copy`, {
        email: signEmail,
        name: signName,
      });
      setShowSignCopy(false);
      setSignEmail('');
      setSignName('');
      loadDocuments();
      loadStats();
    } catch (err) {
      console.error('Sign copy failed', err);
      alert('Failed to create signed copy.');
    } finally {
      setSigning(false);
    }
  };

  const handleInitBucket = async () => {
    try {
      setInitMsg('Initialising...');
      await api.post('/admin/documents/init-bucket');
      setInitMsg('Bucket structure initialised successfully.');
      setTimeout(() => setInitMsg(''), 3000);
    } catch (err) {
      setInitMsg('Failed to initialise bucket.');
      console.error(err);
    }
  };

  const totalPages = Math.ceil(total / limit);

  /* ───────── Render ──────────────────────────────────────────── */
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Documents</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage templates, signed copies, and uploaded documents
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleInitBucket} title="Initialise S3 bucket folders">
            <FolderPlus className="w-4 h-4 mr-1" /> Init Bucket
          </Button>
          <Button size="sm" onClick={() => setShowUpload(true)}>
            <Upload className="w-4 h-4 mr-1" /> Upload
          </Button>
          <Button variant="outline" size="sm" onClick={() => { loadDocuments(); loadStats(); }}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {initMsg && (
        <div className={`text-sm px-3 py-2 rounded ${initMsg.includes('Failed') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {initMsg}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <p className="text-2xl font-bold">{stats.total_documents}</p>
              <p className="text-xs text-muted-foreground">Total Documents</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.templates}</p>
              <p className="text-xs text-muted-foreground">Templates</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.signed_copies}</p>
              <p className="text-xs text-muted-foreground">Signed Copies</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.engagement_docs}</p>
              <p className="text-xs text-muted-foreground">Engagement Docs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <p className="text-2xl font-bold">{fmtBytes(stats.total_bytes)}</p>
              <p className="text-xs text-muted-foreground">Storage Used</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_auto] gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
              />
            </div>
            {/* Folders. Distinct from the storage prefix beside it: that says
                where the bytes live and never changes for an executed copy;
                this is how the team chooses to file things. */}
            <Select value={collectionFilter} onValueChange={(v) => { setCollectionFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="All folders" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All folders</SelectItem>
                {unfiledCount > 0 && (
                  <SelectItem value="__unfiled__">Unfiled ({unfiledCount})</SelectItem>
                )}
                {collections.map((c) => (
                  <SelectItem key={c.collection} value={c.collection}>
                    {c.collection} ({c.documents})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={folderFilter} onValueChange={(v) => { setFolderFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="All Folders" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Folders</SelectItem>
                {FOLDERS.map(f => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full md:w-[160px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Document List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <span>{total} document{total !== 1 ? 's' : ''}</span>
            {totalPages > 1 && (
              <span className="text-sm text-muted-foreground font-normal">
                Page {page} of {totalPages}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="animate-spin w-6 h-6 text-muted-foreground" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No documents found</p>
              <p className="text-sm mt-1">Upload a template or document to get started.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {/* Select-all and the filing bar. The bar appears only when
                  something is selected, so the list is not permanently
                  carrying controls for an action nobody is taking. */}
              <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-muted/40">
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allShownChecked}
                    onChange={toggleAllShown}
                    className="rounded border-gray-300"
                  />
                  {checked.size > 0 ? `${checked.size} selected` : 'Select all on this page'}
                </label>

                {checked.size > 0 && (
                  <div className="flex flex-wrap items-center gap-2 ml-auto">
                    <Select value={moveTo} onValueChange={setMoveTo}>
                      <SelectTrigger className="w-[220px] h-8 text-xs">
                        <SelectValue placeholder="Move to an existing folder" />
                      </SelectTrigger>
                      <SelectContent>
                        {collections.map((c) => (
                          <SelectItem key={c.collection} value={c.collection}>{c.collection}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm" variant="outline" className="h-8 text-xs"
                      disabled={!moveTo || moving}
                      onClick={() => fileSelected(moveTo)}
                    >
                      Move
                    </Button>

                    <span className="text-xs text-muted-foreground">or</span>
                    <Input
                      value={newFolder}
                      onChange={(e) => setNewFolder(e.target.value)}
                      placeholder="New folder name"
                      className="h-8 w-[200px] text-xs"
                    />
                    <Button
                      size="sm" className="h-8 text-xs"
                      disabled={!newFolder.trim() || moving}
                      onClick={() => fileSelected(newFolder.trim())}
                    >
                      <FolderPlus className="w-3.5 h-3.5 mr-1" /> Create &amp; move
                    </Button>

                    <Button
                      size="sm" variant="ghost" className="h-8 text-xs text-muted-foreground"
                      disabled={moving}
                      onClick={() => fileSelected(null)}
                      title="Take these out of their folder"
                    >
                      Unfile
                    </Button>
                  </div>
                )}
              </div>

              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-4 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => { setSelected(doc); setShowDetail(true); }}
                >
                  {/* stopPropagation: ticking a row must not also open it. */}
                  <input
                    type="checkbox"
                    checked={checked.has(doc.id)}
                    onChange={() => toggleOne(doc.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-gray-300 flex-shrink-0"
                    aria-label={`Select ${doc.title}`}
                  />

                  {/* Icon */}
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-muted flex items-center justify-center text-muted-foreground">
                    {mimeIcon(doc.mime_type)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{doc.title}</p>
                      {doc.is_signed_copy && (
                        <Badge variant="outline" className="text-green-700 border-green-300 text-xs">Signed</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {doc.original_filename || doc.s3_key.split('/').pop()}
                    </p>
                  </div>

                  {doc.collection && (
                    <Badge variant="outline" className="text-xs hidden md:inline-flex max-w-[180px] truncate">
                      {doc.collection}
                    </Badge>
                  )}

                  {/* Folder badge */}
                  <Badge className={`${folderColor[doc.folder] || 'bg-gray-100 text-gray-600'} text-xs hidden sm:inline-flex`}>
                    {doc.folder}
                  </Badge>

                  {/* Size & date */}
                  <div className="text-right hidden md:block w-20">
                    <p className="text-xs text-muted-foreground">{fmtBytes(doc.file_size_bytes)}</p>
                  </div>
                  <div className="text-right hidden lg:block w-36">
                    <p className="text-xs text-muted-foreground">{fmtDate(doc.created_at)}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(doc)}
                      title="Download">
                      <Download className="w-4 h-4" />
                    </Button>
                    {doc.folder === 'templates' && !doc.is_signed_copy && (
                      <Button variant="ghost" size="icon" className="h-8 w-8"
                        title="Create signed copy"
                        onClick={() => { setSelected(doc); setShowSignCopy(true); }}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    )}
                    {!doc.is_signed_copy && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                        onClick={() => handleDelete(doc)} title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-3 border-t border-border">
              <Button variant="outline" size="sm" disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}>
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                {page} / {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Upload Dialog ───────────────────────────────────────── */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Folder</label>
              <Select value={uploadFolder} onValueChange={setUploadFolder}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FOLDERS.filter(f => f !== 'signed').map(f => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Category</label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Input value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Optional description" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">File</label>
              <input ref={fileInputRef} type="file" className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.gif,.webp" />
              <div
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2 text-sm min-w-0 overflow-hidden">
                    <FileText className="w-5 h-5 text-primary shrink-0" />
                    <span className="font-medium truncate">{uploadFile.name}</span>
                    <span className="text-muted-foreground shrink-0">({fmtBytes(uploadFile.size)})</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Click to select a file</p>
                    <p className="text-xs text-muted-foreground mt-1">PDF, Word, Excel, Images — up to 50 MB</p>
                  </>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={!uploadFile || uploading}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail Dialog ───────────────────────────────────────── */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {mimeIcon(selected?.mime_type || '')}
              <span className="truncate">{selected?.title}</span>
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <Row label="ID" value={selected.id} />
              <Row label="Folder">
                <Badge className={folderColor[selected.folder] || 'bg-gray-100 text-gray-600'}>
                  {selected.folder}
                </Badge>
              </Row>
              <Row label="Category" value={selected.category} />
              <Row label="Original File" value={selected.original_filename} />
              <Row label="MIME Type" value={selected.mime_type} />
              <Row label="Size" value={fmtBytes(selected.file_size_bytes)} />
              <Row label="S3 Key" value={selected.s3_key} />
              <Row label="Uploaded" value={fmtDate(selected.created_at)} />
              {selected.uploader_email && <Row label="Uploaded By" value={selected.uploader_email} />}
              {selected.description && <Row label="Description" value={selected.description} />}

              {selected.is_signed_copy && (
                <>
                  <hr className="border-border" />
                  <p className="font-medium text-green-700 flex items-center gap-1"><Check className="w-4 h-4" /> Signed Copy</p>
                  {selected.signed_by_email && <Row label="Signed By" value={`${selected.signed_by_name || ''} (${selected.signed_by_email})`} />}
                  {selected.signed_at && <Row label="Signed At" value={fmtDate(selected.signed_at)} />}
                  {selected.source_template_id && <Row label="Source Template" value={selected.source_template_id} />}
                </>
              )}

              {selected.engagement_id && <Row label="Engagement" value={selected.engagement_id} />}
              {selected.deal_id && <Row label="Deal" value={selected.deal_id} />}
              {selected.transaction_id && <Row label="Transaction" value={selected.transaction_id} />}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetail(false)}>Close</Button>
            {selected && (
              <Button onClick={() => handleDownload(selected)}>
                <Download className="w-4 h-4 mr-1" /> Download
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Sign Copy Dialog ────────────────────────────────────── */}
      <Dialog open={showSignCopy} onOpenChange={setShowSignCopy}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Signed Copy</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This creates an immutable copy of <span className="font-medium text-foreground">"{selected?.title}"</span> in
            the <Badge className="bg-green-100 text-green-700 ml-1">signed</Badge> folder. Once created, it cannot be deleted.
          </p>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Signer Email</label>
              <Input value={signEmail} onChange={(e) => setSignEmail(e.target.value)} placeholder="signer@example.com" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Signer Name</label>
              <Input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Jane Doe" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignCopy(false)}>Cancel</Button>
            <Button onClick={handleSignCopy} disabled={signing || !signEmail}>
              {signing ? 'Creating...' : 'Create Signed Copy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* Detail row helper */
const Row: React.FC<{ label: string; value?: string; children?: React.ReactNode }> = ({ label, value, children }) => (
  <div className="flex items-start gap-3">
    <span className="text-muted-foreground w-28 flex-shrink-0">{label}</span>
    <span className="flex-1 break-all">{children || value || '—'}</span>
  </div>
);

export default AdminDocuments;
